import React, { useEffect, useRef, useState } from "react";
import {
  FaBackward,
  FaForward,
  FaPause,
  FaPlay,
  FaTimes,
} from "react-icons/fa";
import ReactPlayer from "react-player";
import { usePubSub, useMeeting } from "@videosdk.live/react-sdk";
import { useMeetingAppContext } from "../../MeetingAppContextDef";
import useIsMobile from "../../hooks/useIsMobile";
import { useSiteContext } from "../../context/SiteContext";

export default function VideoSection({
  fileUrl,
  onClosePanel,
  sideBarContainerWidth,
}) {
  const playerRef = useRef(null);
  const containerRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [ready, setReady] = useState(false);
  const [playedSeconds, setPlayedSeconds] = useState(0);
  const [duration, setDuration] = useState(0);
  const isApplyingRemoteChange = useRef(false);
  const currentVideoUrlRef = useRef(null);
  const pendingPlayCommand = useRef(false); // Queue play command if player not ready
  const isProcessingControl = useRef(false); // Prevent multiple simultaneous control actions
  const isExplicitPause = useRef(false); // Track explicit pause to prevent auto-play interference
  const { sideBarMode, leftSideBarMode } = useMeetingAppContext();
  const { meetingRole } = useSiteContext();
  const isMobile = useIsMobile();
  const mMeeting = useMeeting();

  // PubSub for video control synchronization
  const { publish } = usePubSub("VIDEO_CONTROL", {
    onMessageReceived: (data) => {
      if (!data?.message || data.senderId === mMeeting?.localParticipant?.id) {
        return;
      }

      // Ignore messages if video URL has changed
      const currentUrl = fileUrl.startsWith("http")
        ? fileUrl
        : `https://${fileUrl}`;
      if (
        currentVideoUrlRef.current &&
        currentVideoUrlRef.current !== currentUrl
      ) {
        return;
      }

      try {
        const rawMessage = data.message || data;
        const message = typeof rawMessage === "string" ? JSON.parse(rawMessage) : rawMessage;
        const { eventName, eventData } = message;

        isApplyingRemoteChange.current = true;

        switch (eventName) {
          case "play":
            // Prevent duplicate processing
            if (playing && ready) {
              return;
            }

            // Always update state first - ReactPlayer will handle it via the playing prop
            setPlaying(true);

            if (playerRef.current) {
              if (ready) {
                // Player is ready - ReactPlayer will handle via playing prop
                pendingPlayCommand.current = false;
              } else {
                // Player not ready yet, queue the play command
                pendingPlayCommand.current = true;
              }
            } else {
              // No player ref yet, queue the command
              pendingPlayCommand.current = true;
            }
            break;

          case "pause":
            // Prevent duplicate processing
            if (!playing && ready) {
              return;
            }

            // Update state first
            setPlaying(false);
            pendingPlayCommand.current = false;

            // Directly pause the internal player for immediate response
            if (playerRef.current) {
              try {
                const internalPlayer = playerRef.current.getInternalPlayer();
                if (internalPlayer && !internalPlayer.paused) {
                  internalPlayer.pause();
                }
              } catch (error) {
                console.error("Error pausing video from remote:", error);
              }
            }
            break;

          case "seek":
            if (playerRef.current && eventData?.time !== undefined) {
              // Don't require ready, as seek can work even if player is initializing
              const seekTime = Math.max(
                0,
                Math.min(eventData.time, duration > 0 ? duration : Infinity)
              );
              const wasPlaying = eventData?.wasPlaying ?? playing;

              // Don't process if seeking to same time (with smaller threshold for better sync)
              if (Math.abs(seekTime - playedSeconds) < 0.1) {
                return;
              }

              console.log(
                "Remote seek received: seeking to",
                seekTime,
                "wasPlaying:",
                wasPlaying,
                "current:",
                playedSeconds
              );

              // Apply seek with a flag to prevent progress updates from overwriting
              isApplyingRemoteChange.current = true;

              // Update state immediately - this is critical for sync
              setPlayedSeconds(seekTime);
              setPlaying(wasPlaying);

              // Perform seek - try even if not fully ready
              if (playerRef.current) {
                try {
                  playerRef.current.seekTo(seekTime, "seconds");
                  console.log("Remote seek performed successfully");
                } catch (error) {
                  console.error("Error performing remote seek:", error);
                }
              } else {
                // If not ready, retry after a delay
                setTimeout(() => {
                  if (playerRef.current && ready) {
                    try {
                      playerRef.current.seekTo(seekTime, "seconds");
                    } catch (error) {
                      console.error(
                        "Error performing delayed remote seek:",
                        error
                      );
                    }
                  }
                }, 300);
              }

              // ReactPlayer will handle play/pause state via the playing prop
              // No need to manually call play/pause to avoid conflicts

              setTimeout(() => {
                isApplyingRemoteChange.current = false;
              }, 200);
              return; // Don't clear isApplyingRemoteChange here, it's handled in the timeout
            }
            break;

          case "volume":
            if (playerRef.current && eventData?.volume !== undefined) {
              const internalPlayer = playerRef.current.getInternalPlayer();
              if (internalPlayer) {
                internalPlayer.volume = eventData.volume;
              }
            }
            break;

          default:
            break;
        }

        // Only clear the flag if it wasn't already cleared (seek handles its own cleanup)
        if (eventName !== "seek") {
          setTimeout(() => {
            isApplyingRemoteChange.current = false;
          }, 100);
        }
      } catch (error) {
        console.error("Error parsing video control message:", error);
        isApplyingRemoteChange.current = false;
      }
    },
  });

  useEffect(() => {
    console.log("fileUrl changed:", fileUrl);
    if (fileUrl) {
      // Reset all state when video URL changes
      const newUrl = fileUrl.startsWith("http")
        ? fileUrl
        : `https://${fileUrl}`;
      currentVideoUrlRef.current = newUrl;
      setReady(false);
      setPlaying(false);
      setPlayedSeconds(0);
      setDuration(0);
      isApplyingRemoteChange.current = false;
      pendingPlayCommand.current = false;
      isProcessingControl.current = false;
      isExplicitPause.current = false;
    }
  }, [fileUrl]);

  // Auto-play video when ready and moderator (handles cases where video is set programmatically)
  useEffect(() => {
    if (
      ready &&
      meetingRole === "moderator" &&
      !playing &&
      !isExplicitPause.current &&
      playerRef.current &&
      fileUrl
    ) {
      // Small delay to ensure player is fully initialized
      const timeoutId = setTimeout(() => {
        if (
          playerRef.current &&
          !playing &&
          !isExplicitPause.current &&
          fileUrl
        ) {
          const internalPlayer = playerRef.current.getInternalPlayer();
          if (internalPlayer && internalPlayer.paused) {
            console.log("Auto-playing video for moderator");
            internalPlayer.play().catch((error) => {
              console.error("Error auto-playing video:", error);
            });
            setPlaying(true);
            // Broadcast initial play if moderator
            if (!isApplyingRemoteChange.current) {
              publish(
                JSON.stringify({
                  eventName: "play",
                  eventData: {},
                }),
                { persist: false }
              );
            }
          }
        }
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [ready, meetingRole, playing, publish, fileUrl]);

  const handleReady = () => {
    console.log("Player is ready");
    setReady(true);

    // Auto-play if moderator (participants will follow when moderator plays)
    if (meetingRole === "moderator") {
      // Small delay to ensure player is fully initialized
      setTimeout(() => {
        if (playerRef.current) {
          const internalPlayer = playerRef.current.getInternalPlayer();
          if (internalPlayer) {
            internalPlayer.play().catch((error) => {
              console.error("Error auto-playing video:", error);
            });
          }
          setPlaying(true);
          // Broadcast initial play if moderator
          if (!isApplyingRemoteChange.current) {
            publish(
              JSON.stringify({
                eventName: "play",
                eventData: {},
              }),
              { persist: false }
            );
          }
        }
      }, 200);
    } else {
      // Participant side: Check if there's a pending play command
      if (pendingPlayCommand.current) {
        // Simply set playing state - ReactPlayer will handle it via the playing prop
        setPlaying(true);
        pendingPlayCommand.current = false;

        // Also try direct play as backup after a short delay
        setTimeout(() => {
          if (playerRef.current && ready) {
            const internalPlayer = playerRef.current.getInternalPlayer();
            if (internalPlayer && internalPlayer.paused) {
              internalPlayer.play().catch((error) => {
                console.error("Error playing pending video:", error);
              });
            }
          }
        }, 100);
      }
    }
  };

  const handlePlay = (fromUserAction = true) => {
    // Prevent multiple rapid calls
    if (isProcessingControl.current || playing) {
      return;
    }

    console.log(
      "Video started playing",
      fromUserAction ? "(user action)" : "(from ReactPlayer event)"
    );
    isProcessingControl.current = true;

    // Clear explicit pause flag when playing
    if (fromUserAction) {
      isExplicitPause.current = false;
    }

    // Always update state first - ReactPlayer will handle it via the playing prop
    setPlaying(true);

    // Directly play the internal player if from user action
    if (fromUserAction && playerRef.current) {
      try {
        const internalPlayer = playerRef.current.getInternalPlayer();
        if (internalPlayer && internalPlayer.paused) {
          internalPlayer.play().catch((error) => {
            console.error("Error playing video:", error);
          });
        }
      } catch (error) {
        console.error("Error playing video:", error);
      }
    }

    // Broadcast play event if moderator (only from user action)
    if (
      fromUserAction &&
      meetingRole === "moderator" &&
      !isApplyingRemoteChange.current
    ) {
      publish(
        JSON.stringify({
          eventName: "play",
          eventData: {},
        }),
        { persist: false }
      );
    }

    // Reset processing flag after a short delay
    setTimeout(() => {
      isProcessingControl.current = false;
    }, 200);
  };

  const handlePause = (fromUserAction = true) => {
    // Prevent multiple rapid calls
    if (isProcessingControl.current || !playing) {
      return;
    }

    console.log(
      "Video paused",
      fromUserAction ? "(user action)" : "(from ReactPlayer event)"
    );
    isProcessingControl.current = true;

    // Mark as explicit pause if it's from user action (not from ReactPlayer's onPause event)
    if (fromUserAction) {
      isExplicitPause.current = true;
    }

    // Update state first
    setPlaying(false);

    // Directly pause the internal player for immediate response (only if from user action)
    if (fromUserAction && playerRef.current) {
      try {
        const internalPlayer = playerRef.current.getInternalPlayer();
        if (internalPlayer && !internalPlayer.paused) {
          internalPlayer.pause();
        }
      } catch (error) {
        console.error("Error pausing video:", error);
      }
    }

    // Broadcast pause event if moderator (only from user action)
    if (
      fromUserAction &&
      meetingRole === "moderator" &&
      !isApplyingRemoteChange.current
    ) {
      publish(
        JSON.stringify({
          eventName: "pause",
          eventData: {},
        }),
        { persist: false }
      );
    }

    // Reset processing flag after a short delay
    setTimeout(() => {
      isProcessingControl.current = false;
    }, 200);
  };

  const handleSeek = (seconds, preservePlayingState = true) => {
    if (!playerRef.current) {
      return;
    }

    const seekTime = Math.max(0, Math.min(seconds, duration || 0));
    const wasPlaying = playing;

    // Allow seeks even if currently processing - just debounce slightly
    if (isProcessingControl.current && meetingRole !== "moderator") {
      // Participants: wait a bit if processing
      setTimeout(() => {
        handleSeek(seconds, preservePlayingState);
      }, 100);
      return;
    }

    // Don't process if seeking to same time (with smaller threshold for better sync)
    if (Math.abs(seekTime - playedSeconds) < 0.1) {
      return;
    }

    isProcessingControl.current = true;

    // Update state immediately for both moderator and participants
    setPlayedSeconds(seekTime);

    // Apply seek - try even if not fully ready
    if (ready && playerRef.current) {
      try {
        playerRef.current.seekTo(seekTime, "seconds");
      } catch (error) {
        console.error("Error seeking:", error);
      }
    }

    // Broadcast seek event if moderator (and not during remote change)
    if (meetingRole === "moderator" && !isApplyingRemoteChange.current) {
      console.log("Broadcasting seek event:", { time: seekTime, wasPlaying });
      publish(
        JSON.stringify({
          eventName: "seek",
          eventData: {
            time: seekTime,
            wasPlaying: wasPlaying,
          },
        }),
        { persist: false }
      );
    }

    // Reset processing flag after seek completes
    setTimeout(() => {
      isProcessingControl.current = false;
    }, 200);
  };

  const handleFastForward = () => {
    if (meetingRole === "moderator" && duration > 0) {
      const newTime = Math.min(playedSeconds + 10, duration);
      console.log("Fast forward: seeking to", newTime, "from", playedSeconds);
      handleSeek(newTime, true);
    }
  };

  const handleRewind = () => {
    if (meetingRole === "moderator") {
      const newTime = Math.max(playedSeconds - 10, 0);
      console.log("Rewind: seeking to", newTime, "from", playedSeconds);
      handleSeek(newTime, true);
    }
  };

  const handleSeekBarChange = (e) => {
    if (meetingRole === "moderator" && duration > 0) {
      const seekTime = (e.target.value / 100) * duration;
      // When seeking via seek bar, preserve current playing state
      handleSeek(seekTime, true);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const togglePlayPause = () => {
    if (meetingRole === "moderator") {
      if (playing) {
        handlePause();
      } else {
        handlePlay();
      }
    }
  };

  const handleProgress = (state) => {
    // Only update progress if not applying remote changes
    // This prevents local progress updates from interfering with remote seeks
    if (!isApplyingRemoteChange.current) {
      setPlayedSeconds(state.playedSeconds);
    }
  };

  const handleDuration = (duration) => {
    setDuration(duration);
  };

  const handleError = (error) => {
    console.error("ReactPlayer error:", error);
  };

  const toggleFullscreen = () => {
    if (containerRef.current) {
      if (containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
      } else if (containerRef.current.mozRequestFullScreen) {
        /* Firefox */
        containerRef.current.mozRequestFullScreen();
      } else if (containerRef.current.webkitRequestFullscreen) {
        /* Chrome, Safari & Opera */
        containerRef.current.webkitRequestFullscreen();
      } else if (containerRef.current.msRequestFullscreen) {
        /* IE/Edge */
        containerRef.current.msRequestFullscreen();
      }
    }
  };

  return (
    <div
      className="relative w-full h-full flex flex-col"
      style={{
        height: "100%",
        width: "100%",
        backgroundColor: "#000",
        overflow: "visible",
      }}
      ref={containerRef}
    >
      {/* Close Button */}
      {meetingRole === "moderator" && (
        <button
          onClick={() => onClosePanel()}
          className="absolute top-[12px] right-[16px] z-[99999] bg-black/60 border-none rounded-full w-9 h-9 flex items-center justify-center text-white cursor-pointer hover:bg-black/80 transition-colors"
          aria-label="Close video"
        >
          <FaTimes size={20} />
        </button>
      )}

      {/* Video Player */}
      <div
        className="relative w-full h-full flex items-center justify-center"
        style={{
          width: "100%",
          height: "100%",
          overflow: "visible",
        }}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            position: "relative",
            overflow: "visible",
            zIndex: 1,
          }}
        >
          <ReactPlayer
            ref={playerRef}
            url={fileUrl.startsWith("http") ? fileUrl : `https://${fileUrl}`}
            width="100%"
            height="100%"
            playing={playing}
            volume={0.5}
            onReady={handleReady}
            onPlay={() => handlePlay(false)}
            onPause={() => handlePause(false)}
            onError={handleError}
            onProgress={handleProgress}
            onDuration={handleDuration}
            onSeek={handleSeek}
            controls={false}
            progressInterval={100}
            config={{
              file: {
                attributes: {
                  crossOrigin: "anonymous",
                  preload: "metadata",
                },
              },
            }}
            playsinline
          />

          {/* Custom Control Bar - Only for Moderator */}
          {meetingRole === "moderator" && duration > 0 && (
            <div className="absolute bottom-0 left-0 right-0 bg-black/80 backdrop-blur-sm p-4 z-10">
              {/* Seek Bar */}
              <div className="mb-3">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={duration > 0 ? (playedSeconds / duration) * 100 : 0}
                  onChange={handleSeekBarChange}
                  className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer accent-white"
                  style={{
                    background: `linear-gradient(to right, white 0%, white ${
                      (playedSeconds / duration) * 100
                    }%, #4B5563 ${
                      (playedSeconds / duration) * 100
                    }%, #4B5563 100%)`,
                  }}
                />
              </div>

              {/* Control Buttons and Time */}
              <div className="flex items-center justify-between gap-4">
                {/* Left: Play/Pause and Skip Controls */}
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleRewind}
                    className="p-2 rounded-full bg-white/20 hover:bg-white/30 active:bg-white/40 transition-colors cursor-pointer touch-manipulation"
                    aria-label="Rewind 10 seconds"
                  >
                    <FaBackward className="text-white" size={20} />
                  </button>

                  <button
                    onClick={togglePlayPause}
                    className="p-3 rounded-full bg-white/20 hover:bg-white/30 active:bg-white/40 transition-colors cursor-pointer touch-manipulation"
                    aria-label={playing ? "Pause" : "Play"}
                  >
                    {playing ? (
                      <FaPause className="text-white" size={24} />
                    ) : (
                      <FaPlay className="text-white" size={24} />
                    )}
                  </button>

                  <button
                    onClick={handleFastForward}
                    className="p-2 rounded-full bg-white/20 hover:bg-white/30 active:bg-white/40 transition-colors cursor-pointer touch-manipulation"
                    aria-label="Fast forward 10 seconds"
                  >
                    <FaForward className="text-white" size={20} />
                  </button>
                </div>

                {/* Right: Time Display */}
                <div className="text-white text-sm font-mono">
                  {formatTime(playedSeconds)} / {formatTime(duration)}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
